import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { OrderDetail } from '@furni/shared';
import { Shell } from '../components/Shell';
import { Card, Ornament } from '../components/Card';
import { fetchOrder } from '../lib/api';
import { useSetting } from '../lib/settings';
import { formatSAR } from '../lib/format';

export const Route = createFileRoute('/order/$id')({
  component: OrderPage,
});

function OrderPage() {
  const { t } = useTranslation();
  const { id } = useParams({ from: '/order/$id' });
  const { data, isLoading, error } = useQuery({
    queryKey: ['order', id],
    queryFn: () => fetchOrder(id),
  });

  if (isLoading) {
    return (
      <Shell>
        <div className="pt-10 space-y-4">
          <div className="shimmer-bg animate-shimmer h-24 rounded-3xl" />
          <div className="shimmer-bg animate-shimmer h-40 rounded-3xl" />
          <div className="shimmer-bg animate-shimmer h-32 rounded-3xl" />
        </div>
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell showBack>
        <div className="pt-10 text-oud-800 text-center">
          {error instanceof Error ? error.message : t('upload.errors.generic')}
        </div>
      </Shell>
    );
  }

  return <OrderView order={data} />;
}

const STATUS_LABELS: Record<string, string> = {
  new: 'جديد',
  handling: 'قيد التنفيذ',
  done: 'مكتمل',
  cancelled: 'ملغي',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-amber-50 text-amber-800 border-amber-200',
  handling: 'bg-blue-50 text-blue-800 border-blue-200',
  done: 'bg-green-50 text-green-800 border-green-200',
  cancelled: 'bg-red-50 text-red-800 border-red-200',
};

function OrderView({ order }: { order: OrderDetail }) {
  const { t } = useTranslation();
  const waNumber = useSetting('contact.whatsappNumber');
  const waTemplate = useSetting('contact.whatsappMessage');
  const orderLink = `${window.location.origin}/order/${order.id}`;
  let waText = waTemplate.replace('{{orderId}}', order.id).replace('{{orderLink}}', orderLink);
  if (!waTemplate.includes('{{orderLink}}')) waText += `\n${orderLink}`;
  const waHref = `https://wa.me/${waNumber}?text=${encodeURIComponent(waText)}`;

  return (
    <Shell showBack backTo="/">
      <div className="pt-6 pb-10 space-y-6">
        {/* Confirmation header */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45 }}
          className="flex flex-col items-center text-center space-y-4"
        >
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.1 }}
            className="relative w-16 h-16 rounded-full bg-gradient-to-br from-gold to-oud-700 flex items-center justify-center shadow-quote"
          >
            <span className="absolute inset-0 rounded-full najdi-pattern opacity-30" aria-hidden />
            <svg viewBox="0 0 24 24" fill="none" stroke="#fbf7f2" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="relative w-8 h-8" aria-hidden>
              <path d="M5 12l5 5L20 7" />
            </svg>
          </motion.div>
          <div>
            <h1 className="text-2xl font-black text-oud-900">{t('order_confirmed.title')}</h1>
            <p className="text-sm text-oud-600 mt-1">{t('order_confirmed.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-bold rounded-full px-3 py-1 border ${STATUS_COLORS[order.status] ?? STATUS_COLORS.new}`}>
              {STATUS_LABELS[order.status] ?? order.status}
            </span>
            <span className="text-[11px] tracking-widest text-gold-deep font-bold bg-cream border border-oud-100 rounded-full px-3 py-1">
              #{order.id}
            </span>
          </div>
        </motion.div>

        {/* Customer info */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex-none w-9 h-9 rounded-full bg-oud-800 text-gold-soft flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-oud-900">{order.name}</p>
                <p className="text-sm text-oud-600">{order.phone} {order.city ? `· ${order.city}` : ''}</p>
              </div>
            </div>
            {order.note ? <p className="text-sm text-oud-600 bg-sand rounded-xl px-3 py-2">{order.note}</p> : null}
          </Card>
        </motion.div>

        {/* Order items */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <h2 className="text-sm font-bold text-oud-700 tracking-widest">القطع</h2>
            <Ornament className="flex-1" />
          </div>
          <div className="space-y-2">
            {order.items.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.06 }}
              >
                <a href={`/quote/${item.quote_id}`} className="block">
                  <Card className="flex gap-3 p-0 overflow-hidden">
                    <div className="flex-none w-20 self-stretch bg-sand">
                      {item.image_url ? (
                        <img src={item.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-oud-200">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8" aria-hidden>
                            <path d="M5 10V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4" />
                            <path d="M3 10h18v6H3z" />
                            <path d="M5 16v4M19 16v4" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 py-3 pl-3">
                      <p className="text-sm font-bold text-oud-900 truncate">
                        {item.type ?? '—'}
                        {item.style ? <span className="font-normal text-oud-600"> · {item.style}</span> : null}
                      </p>
                      <p className="text-xs text-oud-500 mt-0.5">
                        {item.qty} × {formatSAR(item.unit_price_sar)}
                      </p>
                      <p className="text-sm font-bold text-gold-deep mt-1">{formatSAR(item.line_total_sar)}</p>
                      {item.notes ? <p className="text-xs text-oud-400 mt-1 truncate">{item.notes}</p> : null}
                    </div>
                  </Card>
                </a>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Price summary */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <Card className="relative overflow-hidden bg-gradient-to-br from-oud-800 to-oud-900 text-sand">
            <div className="absolute inset-0 najdi-pattern opacity-20 pointer-events-none" aria-hidden />
            <div className="relative space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-cream/70">الإجمالي شامل الضريبة</span>
                <span className="text-2xl font-black text-gold-soft">{formatSAR(order.estimated_total_sar)}</span>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* WhatsApp CTA */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="space-y-3 pt-2"
        >
          <p className="text-center text-xs text-oud-600">
            {t('order_confirmed.whatsapp_hint')}
          </p>
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center justify-center gap-2.5 rounded-2xl font-bold h-14 px-6 text-base bg-sage-deep text-sand shadow-soft hover:bg-sage-deep/90 hover:shadow-quote transition-all active:scale-[0.98] w-full"
          >
            <svg viewBox="0 0 32 32" fill="currentColor" className="w-5 h-5" aria-hidden>
              <path d="M16.002 3C8.82 3 3 8.82 3 16c0 2.29.6 4.52 1.74 6.48L3 29l6.7-1.76A12.94 12.94 0 0 0 16 29C23.18 29 29 23.18 29 16S23.18 3 16.002 3Zm0 23.62c-1.98 0-3.92-.53-5.62-1.54l-.4-.24-3.97 1.04 1.06-3.87-.26-.41A10.6 10.6 0 0 1 5.38 16c0-5.86 4.77-10.62 10.63-10.62S26.62 10.14 26.62 16 21.86 26.62 16 26.62Zm6.1-7.95c-.33-.17-1.96-.97-2.26-1.08-.3-.11-.53-.17-.75.17-.22.33-.86 1.08-1.06 1.31-.2.22-.39.25-.72.08-.33-.17-1.4-.52-2.67-1.66-.99-.88-1.65-1.97-1.85-2.3-.2-.33-.02-.51.14-.67.15-.15.33-.39.5-.58.17-.2.22-.33.33-.55.11-.22.06-.42-.03-.58-.08-.17-.75-1.8-1.03-2.47-.27-.65-.54-.56-.75-.57l-.64-.01c-.22 0-.58.08-.88.42-.3.33-1.16 1.13-1.16 2.76 0 1.63 1.19 3.2 1.36 3.42.17.22 2.35 3.58 5.7 5.02.8.35 1.42.55 1.9.71.8.25 1.53.22 2.1.13.64-.09 1.96-.8 2.24-1.58.28-.77.28-1.43.2-1.57-.08-.13-.3-.22-.63-.38Z"/>
            </svg>
            {t('order_confirmed.whatsapp')}
          </a>
          <Link
            to="/"
            className="block text-center text-sm text-oud-600 hover:text-oud-900 font-bold py-3"
          >
            {t('order_confirmed.home')}
          </Link>
        </motion.div>
      </div>
    </Shell>
  );
}
